import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { Block, InputBlock } from '@shared/schema/blocks'
import { isInputBlock } from '@shared/schema/blocks'
import { visibleBlocks, resolveAddressSourceField } from '@shared/engine/visibility'
import type { AnswerValue, StepAnswers, PhotoRef, GpsValue } from '@shared/schema/answers'
import { asStringArray, asPhotoRefs } from '@shared/schema/answers'
import { optionValue } from '@shared/schema/common'

/**
 * ข้อมูลจังหวัด/อำเภอ/ตำบลทั้งประเทศ (~450KB) โหลดแบบ lazy เฉพาะตอนหน้ามีบล็อกที่อยู่จริงเท่านั้น
 * แบบสอบถามส่วนใหญ่ไม่มีบล็อกนี้ จึงไม่ต้องโหลดข้อมูลก้อนนี้เลย (สำคัญกับพนักงานภาคสนามบนเน็ตอ่อน)
 */
type AddressModule = typeof import('@shared/data/thailandAddress')
let addressModulePromise: Promise<AddressModule> | null = null
function loadAddressModule(): Promise<AddressModule> {
  addressModulePromise ??= import('@shared/data/thailandAddress')
  return addressModulePromise
}

/**
 * ★ Renderer ตัวเดียวของทั้งระบบ
 *
 * ใช้ทั้งใน (1) หน้าออกแบบบล็อก (2) พรีวิวมือถือบนกราฟ (3) เว็บแอปจริงของพนักงาน
 * จึงไม่มีทางที่ "ที่ออกแบบไว้" กับ "ที่เห็นจริง" จะต่างกัน — ต่างกันแค่โหมดการโต้ตอบ
 *
 *   run     — กรอกได้จริง บันทึกคำตอบ
 *   preview — แสดงผลเหมือนจริงแต่กดไม่ได้ (พรีวิวบนกราฟ)
 *   design  — เหมือน preview แต่คลิกเพื่อเลือกบล็อกไปแก้ในแผงคุณสมบัติ
 */

export type BlockMode = 'run' | 'preview' | 'design'

/** ตัวจัดการรูปถ่าย — โหมดออนไลน์อัปโหลดทันที, โหมดออฟไลน์เก็บเข้าคิวก่อน */
export type PhotoAdapter = {
  capture: (file: File, fieldName: string) => Promise<PhotoRef>
  urlFor: (assetId: string) => string
  pending?: (assetId: string) => boolean
}

type BlockContextValue = {
  mode: BlockMode
  photo: PhotoAdapter | null
  assetUrl: (assetId: string) => string
  /** บล็อกทั้งหน้า — ช่องอำเภอ/ตำบลต้องใช้หาช่องจังหวัดต้นทางเมื่อไม่ได้ระบุไว้ */
  siblings: readonly Block[]
}

const BlockContext = createContext<BlockContextValue>({
  mode: 'preview',
  photo: null,
  assetUrl: (assetId) => `/api/assets/${assetId}`,
  siblings: [],
})

export type BlockRendererProps = {
  blocks: readonly Block[]
  mode: BlockMode
  values?: StepAnswers
  errors?: Record<string, string>
  onChange?: (fieldName: string, value: AnswerValue) => void
  photo?: PhotoAdapter | null
  assetUrl?: (assetId: string) => string
  /** เฉพาะโหมด design */
  selectedBlockId?: string | null
  onSelectBlock?: (blockId: string) => void
}

export function BlockRenderer({
  blocks,
  mode,
  values = {},
  errors = {},
  onChange,
  photo = null,
  assetUrl = (assetId) => `/api/assets/${assetId}`,
  selectedBlockId = null,
  onSelectBlock,
}: BlockRendererProps) {
  if (blocks.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-400">
        ยังไม่มีเนื้อหาในหน้านี้
      </p>
    )
  }

  // โหมดออกแบบต้องเห็นทุกบล็อกเสมอ (ไม่งั้นแก้บล็อกที่ตั้งเงื่อนไขซ่อนไว้ไม่ได้)
  // ส่วนโหมดใช้งานจริง/พรีวิว แสดงเฉพาะบล็อกที่เงื่อนไขเป็นจริงตามคำตอบตอนนั้น
  const shown = mode === 'design' ? blocks : visibleBlocks(blocks, values)

  return (
    <BlockContext.Provider value={{ mode, photo, assetUrl, siblings: blocks }}>
      <div className="flex flex-col gap-4">
        {shown.map((block) => {
          const content = (
            <BlockView
              block={block}
              value={isInputBlock(block) ? values[block.fieldName] : undefined}
              error={isInputBlock(block) ? errors[block.fieldName] : undefined}
              onChange={onChange}
              values={values}
            />
          )

          if (mode !== 'design') return <div key={block.id}>{content}</div>

          const selected = selectedBlockId === block.id
          return (
            <div
              key={block.id}
              data-block-id={block.id}
              onClick={() => onSelectBlock?.(block.id)}
              className={`relative cursor-pointer rounded-lg p-2 outline-2 outline-offset-2 transition ${
                selected ? 'outline-[var(--fs-primary)] bg-[var(--fs-primary-soft)]/40' : 'outline-transparent hover:outline-slate-300'
              }`}
            >
              {/* ครอบด้วยชั้นโปร่งใสในโหมดออกแบบ เพื่อให้คลิกเลือกบล็อกได้เสมอ
                  และไม่มีการแก้ไขข้อความบน canvas (ต้นเหตุที่ editor เดิมค้าง) */}
              <div className="pointer-events-none">{content}</div>
            </div>
          )
        })}
      </div>
    </BlockContext.Provider>
  )
}

// ---------------------------------------------------------------------------

function BlockView({
  block,
  value,
  error,
  onChange,
  values,
}: {
  block: Block
  value: AnswerValue | undefined
  error: string | undefined
  onChange?: (fieldName: string, value: AnswerValue) => void
  values: StepAnswers
}) {
  switch (block.type) {
    case 'heading': {
      const sizes = { 1: 'text-2xl', 2: 'text-xl', 3: 'text-base' } as const
      const Tag = (['h1', 'h2', 'h3'] as const)[block.level - 1] ?? 'h2'
      return (
        <Tag className={`${sizes[block.level]} font-semibold leading-relaxed text-slate-900`}>
          {block.text}
        </Tag>
      )
    }
    case 'paragraph':
      return <p className="whitespace-pre-wrap leading-relaxed text-slate-600">{block.text}</p>
    case 'divider':
      return <hr className="border-slate-200" />
    case 'spacer': {
      const heights = { sm: 'h-2', md: 'h-5', lg: 'h-10' } as const
      return <div className={heights[block.size]} aria-hidden="true" />
    }
    case 'infoBox': {
      const styles = {
        info: 'bg-sky-50 text-sky-900 border-sky-200',
        warning: 'bg-amber-50 text-amber-900 border-amber-200',
        success: 'bg-emerald-50 text-emerald-900 border-emerald-200',
      } as const
      return (
        <div className={`rounded-lg border px-4 py-3 text-sm leading-relaxed ${styles[block.variant]}`}>
          {block.text}
        </div>
      )
    }
    case 'image':
      return <ImageBlock assetId={block.assetId} alt={block.alt} widthPct={block.widthPct} />
    default:
      return <InputBlockView block={block} value={value} error={error} onChange={onChange} values={values} />
  }
}

function ImageBlock({ assetId, alt, widthPct }: { assetId: string; alt: string; widthPct: number }) {
  const { assetUrl, mode } = useContext(BlockContext)

  // ยังไม่ได้เลือกไฟล์ — ตอนออกแบบขึ้นกรอบไว้ให้เห็นว่ามีบล็อกอยู่ตรงนี้
  // ส่วนตอนพนักงานใช้งานจริงไม่ต้องแสดงอะไรเลย ดีกว่าโชว์รูปแตก
  if (!assetId) {
    if (mode === 'run') return null
    return (
      <div
        style={{ width: `${widthPct}%` }}
        className="mx-auto flex min-h-28 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm leading-relaxed text-slate-500"
      >
        ยังไม่ได้เลือกรูป — กดที่บล็อกนี้แล้วอัปโหลดรูปในแผงตั้งค่าด้านขวา
      </div>
    )
  }

  return (
    <img
      src={assetUrl(assetId)}
      alt={alt}
      loading="lazy"
      style={{ width: `${widthPct}%` }}
      className="mx-auto block h-auto max-w-full rounded-lg"
    />
  )
}

// ---------------------------------------------------------------------------

function FieldFrame({
  block,
  error,
  children,
}: {
  block: InputBlock
  error?: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-2">
      {block.label && (
        <label htmlFor={`field-${block.id}`} className="font-medium leading-relaxed text-slate-800">
          {block.label}
          {block.required && <span className="ms-1 text-red-600">*</span>}
        </label>
      )}
      {children}
      {block.helpText && !error && <p className="text-sm text-slate-500">{block.helpText}</p>}
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  )
}

const controlClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3.5 py-3 text-base leading-relaxed ' +
  'text-slate-900 placeholder:text-slate-400 focus:border-[var(--fs-primary)] focus:outline-2 ' +
  'focus:outline-[var(--fs-primary)] disabled:bg-slate-50 disabled:text-slate-500'

function InputBlockView({
  block,
  value,
  error,
  onChange,
  values,
}: {
  block: Block
  value: AnswerValue | undefined
  error: string | undefined
  onChange?: (fieldName: string, value: AnswerValue) => void
  values: StepAnswers
}) {
  const { mode, siblings } = useContext(BlockContext)
  if (!isInputBlock(block)) return null

  const interactive = mode === 'run'
  const emit = (next: AnswerValue) => onChange?.(block.fieldName, next)
  const invalid = Boolean(error)
  const borderClass = invalid ? 'border-red-500' : ''

  switch (block.type) {
    case 'textInput':
      return (
        <FieldFrame block={block} error={error}>
          <input
            id={`field-${block.id}`}
            name={block.fieldName}
            type={block.inputMode === 'tel' ? 'tel' : 'text'}
            inputMode={block.inputMode === 'number' ? 'decimal' : block.inputMode}
            placeholder={block.placeholder}
            maxLength={block.maxLength}
            disabled={!interactive}
            value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
            onChange={(event) => emit(event.target.value)}
            aria-invalid={invalid || undefined}
            className={`${controlClass} ${borderClass}`}
          />
        </FieldFrame>
      )

    case 'textarea':
      return (
        <FieldFrame block={block} error={error}>
          <textarea
            id={`field-${block.id}`}
            name={block.fieldName}
            rows={block.rows}
            placeholder={block.placeholder}
            maxLength={block.maxLength}
            disabled={!interactive}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => emit(event.target.value)}
            aria-invalid={invalid || undefined}
            className={`${controlClass} resize-y ${borderClass}`}
          />
        </FieldFrame>
      )

    case 'select':
      return (
        <FieldFrame block={block} error={error}>
          <select
            id={`field-${block.id}`}
            name={block.fieldName}
            disabled={!interactive}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => emit(event.target.value)}
            aria-invalid={invalid || undefined}
            className={`${controlClass} ${borderClass}`}
          >
            <option value="">{block.placeholder}</option>
            {block.options.map((option) => (
              <option key={option.id} value={optionValue(option)}>
                {option.label}
              </option>
            ))}
          </select>
        </FieldFrame>
      )

    case 'radioGroup': {
      const current = typeof value === 'string' ? value : ''
      return (
        <FieldFrame block={block} error={error}>
          <div className={block.layout === 'buttons' ? 'flex flex-col gap-2.5' : 'flex flex-col gap-1'}>
            {block.options.map((option) => {
              const optionText = optionValue(option)
              const selected = current === optionText
              return (
                <label
                  key={option.id}
                  className={
                    block.layout === 'buttons'
                      ? `flex min-h-12 cursor-pointer items-center justify-center rounded-xl border-2 px-4 py-3 text-center leading-relaxed transition ${
                          selected
                            ? 'border-[var(--fs-primary)] bg-[var(--fs-primary)] text-[var(--fs-on-primary)] font-medium'
                            : 'border-slate-300 bg-white text-slate-800'
                        } ${interactive ? '' : 'cursor-default'}`
                      : 'flex min-h-12 cursor-pointer items-start gap-3 rounded-lg px-2 py-2.5 leading-relaxed hover:bg-slate-50'
                  }
                >
                  <input
                    type="radio"
                    /* name ผูกกับ id ของบล็อก — สองกลุ่มบนหน้าเดียวกันจึงชนกันไม่ได้เด็ดขาด */
                    name={`radio-${block.id}`}
                    value={optionText}
                    checked={selected}
                    disabled={!interactive}
                    onChange={() => emit(optionText)}
                    className={block.layout === 'buttons' ? 'sr-only' : 'mt-1.5 h-5 w-5 shrink-0'}
                  />
                  <span className={block.layout === 'buttons' ? '' : 'flex-1'}>{option.label}</span>
                </label>
              )
            })}
          </div>
        </FieldFrame>
      )
    }

    case 'checkboxGroup': {
      const selected = asStringArray(value)
      const atLimit = block.maxSelect !== null && selected.length >= block.maxSelect
      return (
        <FieldFrame block={block} error={error}>
          <div className="flex flex-col gap-1">
            {block.options.map((option) => {
              const optionText = optionValue(option)
              const checked = selected.includes(optionText)
              return (
                <label
                  key={option.id}
                  className={`flex min-h-12 items-start gap-3 rounded-lg px-2 py-2.5 leading-relaxed ${
                    interactive && (!atLimit || checked)
                      ? 'cursor-pointer hover:bg-slate-50'
                      : 'cursor-default opacity-60'
                  }`}
                >
                  <input
                    type="checkbox"
                    name={`${block.fieldName}[]`}
                    value={optionText}
                    checked={checked}
                    disabled={!interactive || (atLimit && !checked)}
                    onChange={() =>
                      emit(
                        checked
                          ? selected.filter((item) => item !== optionText)
                          : [...selected, optionText],
                      )
                    }
                    className="mt-1.5 h-5 w-5 shrink-0"
                  />
                  <span className="flex-1">{option.label}</span>
                </label>
              )
            })}
          </div>
          {block.maxSelect !== null && (
            <p className="text-sm text-slate-500">
              เลือกได้ไม่เกิน {block.maxSelect} ข้อ (เลือกแล้ว {selected.length})
            </p>
          )}
        </FieldFrame>
      )
    }

    case 'cameraCapture':
      return (
        <FieldFrame block={block} error={error}>
          <CameraField
            fieldName={block.fieldName}
            maxPhotos={block.maxPhotos}
            photos={asPhotoRefs(value)}
            onChange={(photos) => emit(photos)}
          />
        </FieldFrame>
      )

    case 'gpsCapture':
      return (
        <FieldFrame block={block} error={error}>
          <GpsField
            value={
              value && typeof value === 'object' && !Array.isArray(value) ? (value as GpsValue) : null
            }
            auto={block.auto}
            onChange={(gps) => emit(gps)}
          />
        </FieldFrame>
      )

    case 'provinceSelect':
      return (
        <FieldFrame block={block} error={error}>
          <ProvinceField
            placeholder={block.placeholder}
            value={typeof value === 'string' ? value : ''}
            interactive={interactive}
            onChange={emit}
          />
        </FieldFrame>
      )

    case 'districtSelect':
      return (
        <FieldFrame block={block} error={error}>
          <DistrictField
            placeholder={block.placeholder}
            provinceName={stringField(
              values,
              resolveAddressSourceField(siblings, block.provinceFieldName, 'provinceSelect'),
            )}
            value={typeof value === 'string' ? value : ''}
            interactive={interactive}
            onChange={emit}
          />
        </FieldFrame>
      )

    case 'subdistrictSelect':
      return (
        <FieldFrame block={block} error={error}>
          <SubdistrictField
            placeholder={block.placeholder}
            provinceName={stringField(
              values,
              resolveAddressSourceField(siblings, block.provinceFieldName, 'provinceSelect'),
            )}
            districtName={stringField(
              values,
              resolveAddressSourceField(siblings, block.districtFieldName, 'districtSelect'),
            )}
            value={typeof value === 'string' ? value : ''}
            interactive={interactive}
            onChange={emit}
          />
        </FieldFrame>
      )
  }
}

function stringField(values: StepAnswers, fieldName: string): string {
  const value = fieldName ? values[fieldName] : undefined
  return typeof value === 'string' ? value : ''
}

// ---------------------------------------------------------------------------

function CameraField({
  fieldName,
  maxPhotos,
  photos,
  onChange,
}: {
  fieldName: string
  maxPhotos: number
  photos: PhotoRef[]
  onChange: (photos: PhotoRef[]) => void
}) {
  const { mode, photo, assetUrl } = useContext(BlockContext)
  const interactive = mode === 'run' && photo !== null
  const inputId = `camera-${fieldName}`

  const handleFiles = async (files: FileList | null) => {
    if (!files || !photo) return
    const room = maxPhotos - photos.length
    const chosen = [...files].slice(0, Math.max(room, 0))
    const added: PhotoRef[] = []
    for (const file of chosen) {
      try {
        added.push(await photo.capture(file, fieldName))
      } catch (err) {
        console.error('บันทึกรูปไม่สำเร็จ', err)
      }
    }
    if (added.length > 0) onChange([...photos, ...added])
  }

  return (
    <div className="flex flex-col gap-3">
      {photos.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {photos.map((item) => (
            <div key={item.assetId} className="relative overflow-hidden rounded-lg border border-slate-200">
              <img
                /* รูปที่ยังไม่ได้อัปโหลด ("รอส่ง") ยังไม่มีไฟล์ที่ /api/assets/ — ต้องขอ URL จาก
                   ตัวจัดการรูปก่อนเสมอ (มันจะคืน blob: URL ในเครื่องให้ถ้ายังไม่ได้ส่งขึ้นเซิร์ฟเวอร์) */
                src={photo?.urlFor(item.assetId) ?? assetUrl(item.assetId)}
                alt="รูปที่ถ่าย"
                className="aspect-4/3 w-full object-cover"
              />
              {photo?.pending?.(item.assetId) && (
                <span className="absolute bottom-1 left-1 rounded bg-amber-500 px-1.5 py-0.5 text-xs text-white">
                  รอส่ง
                </span>
              )}
              {interactive && (
                <button
                  type="button"
                  aria-label="ลบรูปนี้"
                  onClick={() => onChange(photos.filter((p) => p.assetId !== item.assetId))}
                  className="absolute end-1 top-1 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white"
                >
                  ลบ
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {photos.length < maxPhotos && (
        <label
          htmlFor={inputId}
          className={`flex min-h-24 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-4 py-5 text-center ${
            interactive
              ? 'cursor-pointer border-slate-300 text-slate-600 hover:border-[var(--fs-primary)] hover:bg-[var(--fs-primary-soft)]'
              : 'border-slate-200 text-slate-400'
          }`}
        >
          <span className="text-2xl" aria-hidden="true">
            📷
          </span>
          <span className="text-sm font-medium">ถ่ายภาพ</span>
          <span className="text-xs">
            {photos.length}/{maxPhotos} รูป
          </span>
          <input
            id={inputId}
            type="file"
            accept="image/*"
            capture="environment"
            multiple={maxPhotos > 1}
            disabled={!interactive}
            className="sr-only"
            onChange={(event) => {
              void handleFiles(event.target.files)
              event.target.value = ''
            }}
          />
        </label>
      )}
    </div>
  )
}

/** ข้อความบอกเหตุที่จับพิกัดไม่ได้ — ต้องบอกให้ชัด ไม่ใช่เงียบไว้เฉย ๆ */
function geoErrorMessage(err: GeolocationPositionError): string {
  if (err.code === err.PERMISSION_DENIED) {
    return 'ไม่ได้รับอนุญาตให้เข้าถึงตำแหน่ง — เปิดสิทธิ์ตำแหน่งให้เบราว์เซอร์แล้วกดจับพิกัดอีกครั้ง'
  }
  if (err.code === err.POSITION_UNAVAILABLE) {
    return 'หาตำแหน่งไม่พบ — ลองออกไปที่โล่งแล้วกดจับพิกัดอีกครั้ง'
  }
  if (err.code === err.TIMEOUT) return 'จับพิกัดนานเกินไป — กดจับพิกัดอีกครั้ง'
  return 'จับพิกัดไม่สำเร็จ — กดจับพิกัดอีกครั้ง'
}

function GpsField({
  value,
  auto,
  onChange,
}: {
  value: GpsValue | null
  auto: boolean
  onChange: (value: GpsValue) => void
}) {
  const { mode } = useContext(BlockContext)
  const interactive = mode === 'run'
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  // onChange เปลี่ยน reference ทุก render — เก็บใน ref เพื่อให้ capture คงที่
  // ไม่งั้น effect จับพิกัดอัตโนมัติจะวิ่งซ้ำไม่รู้จบ
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const capture = useCallback(() => {
    if (!navigator.geolocation) {
      setFailed('อุปกรณ์นี้ไม่รองรับการจับพิกัด')
      return
    }
    setBusy(true)
    setFailed(null)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setBusy(false)
        onChangeRef.current({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        })
      },
      (err) => {
        setBusy(false)
        setFailed(geoErrorMessage(err))
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    )
  }, [])

  /**
   * ★ ตั้งค่า "จับพิกัดอัตโนมัติ" ไว้แล้วต้องจับให้เองตอนเปิดหน้า
   * ของเดิมมีแต่ปุ่มให้กดเอง ทั้งที่หัวข้อคำถามเขียนว่า "(อัตโนมัติ)" —
   * พนักงานจึงกดผ่านไปโดยไม่ได้จับพิกัด ทำให้งานที่เก็บมาไม่มีพิกัดเลยสักรายการ
   * และแผนที่ในหน้าสรุปว่างเปล่าตลอด
   */
  useEffect(() => {
    if (auto && interactive && !value) capture()
    // ตั้งใจให้ทำงานครั้งเดียวตอน mount — ไม่ต้องจับใหม่ทุกครั้งที่ value เปลี่ยน
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, interactive, capture])

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm leading-relaxed text-slate-600">
          {value
            ? `${value.lat.toFixed(5)}, ${value.lng.toFixed(5)}`
            : busy
              ? 'กำลังจับพิกัด...'
              : 'ยังไม่ได้จับพิกัด'}
        </span>
        <button
          type="button"
          disabled={!interactive || busy}
          onClick={capture}
          className="ms-auto min-h-11 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-50"
        >
          {value ? 'จับพิกัดใหม่' : 'จับพิกัด'}
        </button>
      </div>
      {failed && <p className="mt-2 text-sm leading-relaxed text-red-600">{failed}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ที่อยู่แบบเลือกต่อกัน (จังหวัด → อำเภอ → ตำบล) — ข้อมูลโหลดแบบ lazy ตัวเดียวใช้ร่วมกัน 3 บล็อก
// ---------------------------------------------------------------------------

/** เก็บโมดูลข้อมูลไว้ใน state เดียว ทุกบล็อกที่ mount เรียก loadAddressModule() ซ้ำได้ฟรี (แคชแล้ว) */
function useAddressModule(): AddressModule | null {
  const [mod, setMod] = useState<AddressModule | null>(null)
  useEffect(() => {
    let cancelled = false
    loadAddressModule().then((loaded) => {
      if (!cancelled) setMod(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return mod
}

function ProvinceField({
  placeholder,
  value,
  interactive,
  onChange,
}: {
  placeholder: string
  value: string
  interactive: boolean
  onChange: (value: string) => void
}) {
  const mod = useAddressModule()
  const provinces = mod?.listProvinces() ?? []

  return (
    <select
      disabled={!interactive || !mod}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={controlClass}
    >
      <option value="">{mod ? placeholder : 'กำลังโหลดรายชื่อจังหวัด...'}</option>
      {provinces.map((province) => (
        <option key={province.id} value={province.name}>
          {province.name}
        </option>
      ))}
    </select>
  )
}

function DistrictField({
  placeholder,
  provinceName,
  value,
  interactive,
  onChange,
}: {
  placeholder: string
  provinceName: string
  value: string
  interactive: boolean
  onChange: (value: string) => void
}) {
  const mod = useAddressModule()
  const provinceId = mod && provinceName ? mod.findProvinceIdByName(provinceName) : undefined
  const districts = mod && provinceId !== undefined ? mod.listDistricts(provinceId) : []

  useEffect(() => {
    // เปลี่ยนจังหวัดแล้วอำเภอเดิมไม่อยู่ในจังหวัดใหม่ — ล้างทิ้งกันค่าไม่ตรงกันค้างอยู่
    if (mod && value && !districts.some((district) => district.name === value)) onChange('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mod, provinceName, value])

  return (
    <select
      disabled={!interactive || !mod || !provinceName}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={controlClass}
    >
      <option value="">{!provinceName ? 'เลือกจังหวัดก่อน' : mod ? placeholder : 'กำลังโหลด...'}</option>
      {districts.map((district) => (
        <option key={district.id} value={district.name}>
          {district.name}
        </option>
      ))}
    </select>
  )
}

function SubdistrictField({
  placeholder,
  provinceName,
  districtName,
  value,
  interactive,
  onChange,
}: {
  placeholder: string
  provinceName: string
  districtName: string
  value: string
  interactive: boolean
  onChange: (value: string) => void
}) {
  const mod = useAddressModule()
  const provinceId = mod && provinceName ? mod.findProvinceIdByName(provinceName) : undefined
  const districtId =
    mod && districtName && provinceId !== undefined
      ? mod.findDistrictIdByName(districtName, provinceId)
      : undefined
  const subdistricts = mod && districtId !== undefined ? mod.listSubdistricts(districtId) : []

  useEffect(() => {
    if (mod && value && !subdistricts.some((sub) => sub.name === value)) onChange('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mod, provinceName, districtName, value])

  return (
    <select
      disabled={!interactive || !mod || !districtName}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={controlClass}
    >
      <option value="">{!districtName ? 'เลือกอำเภอ/เขตก่อน' : mod ? placeholder : 'กำลังโหลด...'}</option>
      {subdistricts.map((sub) => (
        <option key={sub.id} value={sub.name}>
          {sub.name}
        </option>
      ))}
    </select>
  )
}
